import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { reportClientError } from "@/lib/reportError";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);

    // `captureReactException` e NAO `captureException`: so ele liga o
    // componentStack como causa encadeada, produzindo a arvore de componentes
    // legivel no Sentry. Com captureException esse rastro se perde.
    // No-op se o Sentry nao foi inicializado (sem VITE_SENTRY_DSN).
    Sentry.captureReactException(error, info);

    // Mantido: alimenta `client_errors` + alerta de Telegram, que e o caminho
    // que hoje aparece no docs/ISSUES.md. Sentry e profundidade, nao substituto.
    reportClientError({
      type: "crash",
      message: `${error.name}: ${error.message}`,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
          <h2 className="text-lg font-semibold text-foreground mb-2">
            Algo deu errado
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Ocorreu um erro inesperado. Tente recarregar o app.
          </p>
          <button
            onClick={this.handleReload}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium"
          >
            Recarregar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
